import { Avatar, Button, Chip, Collapse, Divider, Link, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { ArrowRight, ArrowUp, ClockCountdown, UserList } from "@phosphor-icons/react";
import React from "react";
import MatchDetailItem from "./MatchDetailItem";
import ApprovingFlowBlock from "./ApprovingFlowBlock";
import getAvatarInitials from "utils/getAvatarInitials";

export default function ClinicianCard({
  collapsed,
  generatedSchedule,
  isLoading,
  isSuccessful,
  onApproveAll,
  onSetTentative,
  setShowCaseload,
  ...props
}) {
  const [scheduleHours, setScheduleHours] = React.useState(0);
  const { clinician_name, client_schedules, current_case_load, new_case_load } = generatedSchedule;
  const totalMatches = client_schedules.length;
  const theme = useTheme();

  React.useEffect(() => {
    setScheduleHours(Number(current_case_load || 0) + Number(new_case_load || 0));
  }, [current_case_load, new_case_load]);
  const clinicianFirstName = clinician_name.split(" ")[0];
  const cardStyleProps = {
    padding: 5,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "16px",
    bgcolor: theme.palette.primary.contrast,
    sx: { "&:hover": { bgcolor: "default.hover", cursor: "pointer" } },
  };

  if (isLoading || isSuccessful) {
    return (
      <ApprovingFlowBlock isLoading={isLoading} isSuccessful={isSuccessful} {...cardStyleProps} />
    );
  }

  return (
    <Stack {...cardStyleProps} {...props}>
      <Stack gap={5}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={2}>
            <UserList
              size={16}
              weight="duotone"
              style={{ flexShrink: 0 }}
              color={theme.palette.action.active}
            />
            <Typography
              color="text.secondary"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "20px",
                letterSpacing: "0.14px",
              }}
            >
              Match
            </Typography>
          </Stack>
          {/* Will be implemented in a future release */}
          {/* {!collapsed && (
            <Link
              underline="none"
              onClick={(e) => {
                e.stopPropagation(); // Prevent the card from collapsing
                setShowCaseload(true);
              }}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="caption">View {clinicianFirstName}'s caseload</Typography>
                <ArrowRight size={16} />
              </Stack>
            </Link>
          )} */}
        </Stack>
        <Stack direction="row" justifyContent="space-between" gap={3} alignItems="center">
          <Stack direction="row" gap={3}>
            <Avatar size="24px">{getAvatarInitials(clinician_name)}</Avatar>
            <Typography variant="body1">{clinician_name}</Typography>
          </Stack>
          <Divider orientation="horizontal" sx={{ flex: "auto" }} />
          <Chip
            label={`${totalMatches} Match${totalMatches === 1 ? "" : "es"}`}
            variant="outlined"
            size="small"
            color="warning"
          />
        </Stack>
      </Stack>
      <Collapse in={!collapsed && !(isLoading || isSuccessful)} timeout="auto">
        <Stack gap={5} mt={5}>
          <Stack gap={1}>
            <MatchDetailItem
              Icon={ClockCountdown}
              iconProps={{ weight: "duotone" }}
              text={`Adds *${new_case_load} hours* to ${clinicianFirstName}’s caseload each week`}
            />
            <MatchDetailItem
              Icon={ArrowUp}
              text={`Scheduled hours total *${scheduleHours} hours*`}
            />
          </Stack>
          <Stack direction={"row"} gap={3}>
            <Button sx={{ flex: "auto" }} size="small" onClick={onApproveAll}>
              Approve All
            </Button>
            <Button color="secondary" size="small" onClick={onSetTentative}>
              Set All As Tentative
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
}
